import { useState, useEffect, useRef } from 'react'

import {
  ActionIcon,
  Badge,
  Divider,
  Drawer,
  Loader,
  PasswordInput,
  Select,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { Check, X, Pencil, Plus, Trash2, Clock } from 'lucide-react'

import { useMutation, useQuery } from '@redwoodjs/web'

import { useAuth } from 'src/auth'
import DNButton from 'src/components/Buttons/Button/DNButton'
import PhoneInput from 'src/components/Inputs/PhoneInput/PhoneInput'
import { toast } from 'src/components/Overlays/Toast/Toast'
import CallerIdStatusIcon from 'src/components/VerifiedCallerId/CallerIdStatusIcon'
import { EXCHANGE_AUTH_TOKEN } from 'src/graphql/email.graphql'
import {
  GET_EMPLOYEE_BY_ID,
  GET_EMPLOYEES_ONLY,
  UPDATE_EMPLOYEE,
  CONFIGURE_EMPLOYEE_CALENDAR,
} from 'src/graphql/employees.graphql'
import {
  GET_EMPLOYEE_PERMISSIONS,
  GET_ALL_PERMISSIONS,
  GRANT_PERMISSION,
  REVOKE_PERMISSION,
} from 'src/graphql/permissions.graphql'
import { VerifiedCallerIdOwnerType } from 'src/graphql/types/verifiedCallerIds'
import {
  ENABLE_MFA,
  DISABLE_MFA,
  RESET_MFA,
  BEFORE_LOGOUT,
} from 'src/graphql/users.graphql'
import IconCheckmark from 'src/icons/IconCheckmark'
import IconClose from 'src/icons/IconClose'
import IconInfoCircle from 'src/icons/IconInfoCircle'
import { hasPermission, isAdmin, isAdminOrOps } from 'src/lib/auth/permissions'
import { PERMISSION_KEYS } from 'src/lib/permissions'
import { formatPhone, unformatPhoneNumber } from 'src/lib/phone.utils'

import { updateEmployeeSchema } from '../employeeForm.schema'

import PermissionHistoryModal from './PermissionHistoryModal'

const nylasAuthUrl = `https://api.us.nylas.com/v3/connect/auth?client_id=${process.env.NYLAS_CLIENT_ID}&redirect_uri=${window.location.origin}/oauth-nylas-callback&response_type=code&access_type=online&state=sQ6vFQN`

interface UserDetailsDrawerProps {
  opened: boolean
  close: () => void
  employeeId: string | null
  refetchEmployees: () => void
}

const UserDetailsDrawer = ({
  opened,
  close,
  employeeId,
  refetchEmployees,
}: UserDetailsDrawerProps) => {
  const { currentUser, logOut } = useAuth()
  const [isEditMode, setIsEditMode] = useState(false)
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [phone, setPhone] = useState('')
  const [jobTitle, setJobTitle] = useState('')
  const [roleType, setRoleType] = useState<string>('')
  const [calendarApiKey, setCalendarApiKey] = useState('')
  const [selectedPermission, setSelectedPermission] = useState<string | null>(
    null
  )
  const [historyOpened, { open: openHistory, close: closeHistory }] =
    useDisclosure(false)
  const [errors, setErrors] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    jobTitle: '',
  })
  const hasExchangedToken = useRef(false)

  const { data, loading, refetch } = useQuery(GET_EMPLOYEE_BY_ID, {
    variables: {
      id: employeeId || '',
    },
    skip: !employeeId,
  })

  const employee = data?.employee
  const isAdminOrOpsUser = isAdminOrOps(currentUser)
  const isOwnProfile = currentUser?.email === employee?.email
  const isAdminUser = isAdmin(currentUser)
  const canManageUserAccess =
    isAdminUser ||
    hasPermission(currentUser, PERMISSION_KEYS.MANAGE_USER_ACCESS)

  // Query employee permissions
  const {
    data: permissionsData,
    loading: permissionsLoading,
    refetch: refetchPermissions,
  } = useQuery(GET_EMPLOYEE_PERMISSIONS, {
    variables: {
      employeeId: employeeId || '',
    },
    skip: !employeeId || !canManageUserAccess,
  })

  // Query all available permissions
  const { data: allPermissionsData, loading: allPermissionsLoading } = useQuery(
    GET_ALL_PERMISSIONS,
    {
      skip: !canManageUserAccess,
    }
  )

  const employeePermissions = permissionsData?.employeePermissions || []
  const allPermissions = allPermissionsData?.permissions || []

  // Filter out already granted permissions that are active (not revoked)
  const activePermissions = employeePermissions.filter((ep) => !ep.revokedAt)
  const availablePermissions = allPermissions.filter(
    (p) => !activePermissions.some((ep) => ep.permissionKey === p.key)
  )

  // Handle email OAuth callback
  const queryParams = new URLSearchParams(window.location.search)
  const nylasCode = queryParams.get('nylasCode')

  const [exchangeAuthToken] = useMutation(EXCHANGE_AUTH_TOKEN, {
    onCompleted: (data) => {
      if (data.exchangeAuthToken.emailConnected) {
        toast('Email connected successfully', 'success')
        refetch()
        refetchEmployees()
      }
    },
    onError: (error) => {
      toast(error.message || 'Failed to connect email', 'error')
    },
  })

  useEffect(() => {
    if (nylasCode && !hasExchangedToken.current) {
      hasExchangedToken.current = true
      exchangeAuthToken({ variables: { input: { code: nylasCode } } })
    }
  }, [nylasCode, exchangeAuthToken])

  // MFA mutations
  const [beforeLogout] = useMutation(BEFORE_LOGOUT)

  const [enableMFA, { loading: enablingMFA }] = useMutation(ENABLE_MFA, {
    onCompleted: async (data) => {
      if (data?.enableMFA?.success) {
        toast(
          data.enableMFA.message ||
            'MFA enabled successfully. Please log in again to complete setup.',
          'success'
        )
        try {
          await beforeLogout()
        } catch (_) {
          // ignore
        }
        await logOut({
          logoutParams: { returnTo: window.location.origin },
        })
      }
    },
    onError: (error) => {
      toast(error.message || 'Failed to enable MFA', 'error')
    },
  })

  const [disableMFA, { loading: disablingMFA }] = useMutation(DISABLE_MFA, {
    onCompleted: (data) => {
      if (data?.disableMFA?.success) {
        toast(data.disableMFA.message || 'MFA disabled successfully', 'success')
        refetch()
        refetchEmployees()
      }
    },
    onError: (error) => {
      toast(error.message || 'Failed to disable MFA', 'error')
    },
  })

  const [resetMFA, { loading: resettingMFA }] = useMutation(RESET_MFA, {
    onCompleted: (data) => {
      if (data?.resetMFA?.success) {
        toast(data.resetMFA.message || 'MFA reset successfully', 'success')
        refetch()
        refetchEmployees()
      }
    },
    onError: (error) => {
      toast(error.message || 'Failed to reset MFA', 'error')
    },
  })

  // Grant permission mutation
  const [grantPermission, { loading: grantingPermission }] = useMutation(
    GRANT_PERMISSION,
    {
      onCompleted: () => {
        toast('Permission granted successfully', 'success')
        setSelectedPermission(null)
        refetchPermissions()
        refetch()
        refetchEmployees()
      },
      onError: (error) => {
        toast(error.message || 'Failed to grant permission', 'error')
      },
    }
  )

  // Revoke permission mutation
  const [revokePermission, { loading: revokingPermission }] = useMutation(
    REVOKE_PERMISSION,
    {
      onCompleted: () => {
        toast('Permission revoked successfully', 'success')
        refetchPermissions()
        refetch()
        refetchEmployees()
      },
      onError: (error) => {
        toast(error.message || 'Failed to revoke permission', 'error')
      },
    }
  )

  const handleGrantPermission = () => {
    if (!selectedPermission || !employeeId) return

    grantPermission({
      variables: {
        input: {
          employeeId,
          permissionKey: selectedPermission,
        },
      },
    })
  }

  const handleRevokePermission = (employeePermissionId: string) => {
    revokePermission({
      variables: {
        input: {
          employeePermissionId,
        },
      },
    })
  }

  // Calendar API key configuration
  const [configureEmployeeCalendar] = useMutation(CONFIGURE_EMPLOYEE_CALENDAR, {
    onCompleted: () => {
      setCalendarApiKey('')
      refetch()
      refetchEmployees()
      toast('Calendar API key updated successfully!', 'success')
    },
    onError: (error) => {
      setCalendarApiKey('')
      refetch()
      refetchEmployees()
      toast(error.message, 'error')
    },
  })

  // Debounce calendar API key updates while editing and when the field is visible.
  useEffect(() => {
    const newKey = calendarApiKey.trim()
    const canEditCalendarKey =
      isEditMode &&
      (currentUser?.email === employee?.email || isAdminOrOpsUser)

    if (!canEditCalendarKey || !newKey) return

    const timeoutId = setTimeout(() => {
      configureEmployeeCalendar({
        variables: {
          id: employeeId,
          calendarAPIKey: newKey,
        },
      })
    }, 2000)

    return () => clearTimeout(timeoutId)
  }, [
    calendarApiKey,
    configureEmployeeCalendar,
    employeeId,
    isEditMode,
    currentUser,
    employee,
    isAdminOrOpsUser,
  ])

  useEffect(() => {
    // Only sync from the server when not editing; otherwise refetches can wipe in-progress edits.
    if (employee && !isEditMode) {
      setFirstName(employee.firstName || '')
      setLastName(employee.lastName || '')
      setPhone(employee.phone || '')
      setJobTitle(employee.jobTitle || '')
      setRoleType(employee.roleType || '')
      setCalendarApiKey('')
      setErrors({ firstName: '', lastName: '', phone: '', jobTitle: '' })
    }
  }, [employee, isEditMode])

  const validateFields = () => {
    const result = updateEmployeeSchema.safeParse({
      firstName,
      lastName,
      phone,
      jobTitle,
    })

    const newErrors = {
      firstName: '',
      lastName: '',
      phone: '',
      jobTitle: '',
    }

    if (!result.success) {
      for (const issue of result.error.issues) {
        const field = issue.path[0] as keyof typeof newErrors
        if (field in newErrors && !newErrors[field]) {
          newErrors[field] = issue.message
        }
      }
    }

    return newErrors
  }

  const validateForm = () => {
    const newErrors = validateFields()
    setErrors(newErrors)

    return Object.values(newErrors).every((error) => error === '')
  }

  const [updateEmployee, { loading: updating }] = useMutation(UPDATE_EMPLOYEE, {
    refetchQueries: [GET_EMPLOYEES_ONLY],
    onCompleted: () => {
      toast('User updated successfully', 'success')
      setIsEditMode(false)
      setErrors({ firstName: '', lastName: '', phone: '', jobTitle: '' })
      refetch()
      refetchEmployees()
    },
    onError: (error) => {
      toast(error.message || 'Failed to update user', 'error')
    },
  })

  const handleSave = () => {
    if (!validateForm()) {
      return
    }

    const input: any = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      phone: unformatPhoneNumber(phone),
      jobTitle: jobTitle.trim(),
    }

    // Only include roleType if admin and if it has changed
    if (isAdminUser && roleType && roleType !== employee?.roleType) {
      input.roleType = roleType
    }

    updateEmployee({
      variables: {
        id: employeeId,
        input,
      },
    })
  }

  const handleCancel = () => {
    // Reset to original values
    if (employee) {
      setFirstName(employee.firstName || '')
      setLastName(employee.lastName || '')
      setPhone(employee.phone || '')
      setJobTitle(employee.jobTitle || '')
      setRoleType(employee.roleType || '')
    }
    setErrors({ firstName: '', lastName: '', phone: '', jobTitle: '' })
    setSelectedPermission(null)
    setIsEditMode(false)
  }

  const isFormValid = () =>
    Object.values(validateFields()).every((error) => error === '')

  return (
    <Drawer
      opened={opened}
      onClose={close}
      position="right"
      withCloseButton={false}
      size={'30%'}
      offset={6}
      radius="md"
      overlayProps={{ backgroundOpacity: 0.2 }}
      classNames={{
        body: 'flex flex-col gap-4 h-full',
      }}
    >
      <div className="flex h-full flex-col gap-4">
        {/* Header */}
        <div className="flex flex-row items-center justify-between">
          <Text size="lg" fw={500}>
            User Details
          </Text>
          <div className="flex items-center gap-2">
            {!isEditMode && !updating && (
              <DNButton
                variant="transparent"
                onClick={() => setIsEditMode(true)}
              >
                <Pencil size={12} />
                Edit
              </DNButton>
            )}
            {isEditMode && !updating && (
              <>
                <Tooltip
                  label={
                    !isFormValid()
                      ? 'Please fix form errors before saving'
                      : 'Save changes'
                  }
                  position="bottom"
                  disabled={isFormValid()}
                >
                  <DNButton
                    variant="transparent"
                    onClick={handleSave}
                    disabled={!isFormValid()}
                    className="text-doubleNickel-success-500"
                  >
                    <Check size={14} />
                    Save
                  </DNButton>
                </Tooltip>
                <DNButton
                  variant="transparent"
                  onClick={handleCancel}
                  className="text-doubleNickel-error-500"
                >
                  <X size={14} />
                  Cancel
                </DNButton>
              </>
            )}
            {updating && <Loader size={12} color="gray" />}
          </div>
        </div>

        <Divider variant="dashed" />

        {loading ? (
          <div className="flex flex-1 items-center justify-center">
            <Loader size="sm" color="gray" />
          </div>
        ) : (
          <div className="flex flex-col gap-3 overflow-y-auto">
            {/* OVERVIEW Section */}
            <div className="space-y-4">
              <Text size="xs" fw={400} className="text-doubleNickel-gray-500">
                OVERVIEW
              </Text>

              <div className="space-y-3">
                <div className="flex flex-col gap-1">
                  <div className="flex flex-row items-center justify-between">
                    <Text size="xs">
                      First Name{' '}
                      {isEditMode && (
                        <span className="text-doubleNickel-error-500">*</span>
                      )}
                    </Text>
                    {isEditMode ? (
                      <TextInput
                        size="xs"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        autoComplete="given-name"
                        className="w-[250px]"
                        classNames={{
                          input: 'text-xs text-right',
                        }}
                        error={errors.firstName}
                      />
                    ) : (
                      <Text size="xs" c="gray.9">
                        {employee?.firstName}
                      </Text>
                    )}
                  </div>
                  {isEditMode && errors.firstName && (
                    <Text size="xs" c="red" className="text-right">
                      {errors.firstName}
                    </Text>
                  )}
                </div>

                <div className="flex flex-col gap-1">
                  <div className="flex flex-row items-center justify-between">
                    <Text size="xs">
                      Last Name{' '}
                      {isEditMode && (
                        <span className="text-doubleNickel-error-500">*</span>
                      )}
                    </Text>
                    {isEditMode ? (
                      <TextInput
                        size="xs"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        autoComplete="family-name"
                        className="w-[250px]"
                        classNames={{
                          input: 'text-xs text-right',
                        }}
                        error={errors.lastName}
                      />
                    ) : (
                      <Text size="xs" c="gray.9">
                        {employee?.lastName}
                      </Text>
                    )}
                  </div>
                  {isEditMode && errors.lastName && (
                    <Text size="xs" c="red" className="text-right">
                      {errors.lastName}
                    </Text>
                  )}
                </div>

                <div className="flex flex-row items-center justify-between">
                  <Text size="xs">Email</Text>
                  <Text size="xs" c="gray.9">
                    {employee?.email}
                  </Text>
                </div>

                <div className="flex flex-col gap-1">
                  <div className="flex flex-row items-center justify-between">
                    <Text size="xs">
                      Phone{' '}
                      {isEditMode && (
                        <span className="text-doubleNickel-error-500">*</span>
                      )}
                    </Text>
                    {isEditMode ? (
                      <PhoneInput
                        size="xs"
                        value={phone}
                        onChange={(val) => setPhone(val)}
                        autoComplete="tel"
                        className="w-[250px]"
                        classNames={{
                          input: 'text-xs text-right',
                        }}
                        error={errors.phone}
                      />
                    ) : (
                      <div className="flex flex-row items-center gap-2">
                        <Text size="xs" c="gray.9">
                          {employee?.phone ? formatPhone(employee.phone) : '—'}
                        </Text>
                        {employee?.phone && (
                          <CallerIdStatusIcon
                            ownerType={VerifiedCallerIdOwnerType.EMPLOYEE}
                            employeeId={employee.employeeId}
                            phoneNumber={employee.phone}
                            caller={isOwnProfile ? 'you' : 'they'}
                            size={14}
                            canManage={isOwnProfile || isAdminOrOpsUser}
                          />
                        )}
                      </div>
                    )}
                  </div>
                  {isEditMode && errors.phone && (
                    <Text size="xs" c="red" className="text-right">
                      {errors.phone}
                    </Text>
                  )}
                </div>

                <div className="flex flex-col gap-1">
                  <div className="flex flex-row items-center justify-between">
                    <Text size="xs">
                      Job Title{' '}
                      {isEditMode && (
                        <span className="text-doubleNickel-error-500">*</span>
                      )}
                    </Text>
                    {isEditMode ? (
                      <TextInput
                        size="xs"
                        value={jobTitle}
                        onChange={(e) => setJobTitle(e.target.value)}
                        autoComplete="organization-title"
                        className="w-[250px]"
                        classNames={{
                          input: 'text-xs text-right',
                        }}
                        error={errors.jobTitle}
                      />
                    ) : (
                      <Text size="xs" c="gray.9">
                        {employee?.jobTitle || '—'}
                      </Text>
                    )}
                  </div>
                  {isEditMode && errors.jobTitle && (
                    <Text size="xs" c="red" className="text-right">
                      {errors.jobTitle}
                    </Text>
                  )}
                </div>

                {isAdminUser && (
                  <div className="flex flex-row items-center justify-between">
                    <Text size="xs">Role</Text>
                    {isEditMode ? (
                      <Select
                        size="xs"
                        value={roleType}
                        onChange={(value) => setRoleType(value || '')}
                        data={[
                          { value: 'ADMIN', label: 'Admin' },
                          { value: 'RECRUITER', label: 'Recruiter' },
                          { value: 'OPS', label: 'Ops' },
                        ]}
                        className="w-[250px]"
                        classNames={{
                          input: 'text-xs text-right',
                        }}
                      />
                    ) : (
                      <Text size="xs" c="gray.9">
                        {employee?.roleType || '—'}
                      </Text>
                    )}
                  </div>
                )}
              </div>
            </div>

            <Divider variant="dashed" />

            {/* PERMISSIONS Section */}
            {canManageUserAccess && (
              <>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Text
                      size="xs"
                      fw={400}
                      className="text-doubleNickel-gray-500"
                    >
                      PERMISSIONS
                    </Text>
                    <Tooltip label="Permission history">
                      <ActionIcon
                        size="sm"
                        variant="subtle"
                        color="gray"
                        onClick={openHistory}
                      >
                        <Clock size={14} />
                      </ActionIcon>
                    </Tooltip>
                  </div>

                  <div className="space-y-3">
                    {/* Active Permissions List */}
                    <div className="space-y-2">
                      {permissionsLoading ? (
                        <div className="flex justify-center py-2">
                          <Loader size="xs" color="gray" />
                        </div>
                      ) : activePermissions.length > 0 ? (
                        activePermissions.map((ep) => (
                          <div
                            key={ep.employeePermissionId}
                            className="flex items-center justify-between rounded-md border border-doubleNickel-gray-200 bg-doubleNickel-gray-50 px-3 py-2"
                          >
                            <div className="flex flex-col gap-1">
                              <Text size="xs" fw={500}>
                                {ep.permission?.description || ep.permissionKey}
                              </Text>
                              {ep.permission?.category && (
                                <Badge
                                  size="xs"
                                  variant="light"
                                  className="w-fit"
                                >
                                  {ep.permission.category}
                                </Badge>
                              )}
                            </div>
                            {isEditMode && (
                              <Tooltip label="Revoke permission">
                                <ActionIcon
                                  size="sm"
                                  variant="subtle"
                                  color="red"
                                  onClick={() =>
                                    handleRevokePermission(
                                      ep.employeePermissionId
                                    )
                                  }
                                  disabled={revokingPermission}
                                >
                                  <Trash2 size={14} />
                                </ActionIcon>
                              </Tooltip>
                            )}
                          </div>
                        ))
                      ) : (
                        <Text size="xs" c="gray.6" className="py-4 text-center">
                          No permissions granted
                        </Text>
                      )}
                    </div>

                    {/* Add Permission */}
                    {isEditMode && (
                      <div className="flex items-end gap-2">
                        <Select
                          size="xs"
                          placeholder="Select permission to grant"
                          value={selectedPermission}
                          onChange={setSelectedPermission}
                          data={availablePermissions.map((p) => ({
                            value: p.key,
                            label: p.description || p.key,
                          }))}
                          className="flex-1"
                          searchable
                          disabled={allPermissionsLoading}
                        />
                        <DNButton
                          variant="secondary"
                          size="xs"
                          onClick={handleGrantPermission}
                          disabled={!selectedPermission || grantingPermission}
                        >
                          <Plus size={14} />
                          Grant
                        </DNButton>
                      </div>
                    )}
                  </div>
                </div>

                <Divider variant="dashed" />
              </>
            )}

            {/* CONNECTIONS Section */}
            <div className="space-y-4">
              <Text size="xs" fw={400} className="text-doubleNickel-gray-500">
                CONNECTIONS
              </Text>

              <div className="space-y-3">
                <div className="flex flex-col gap-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1">
                      <Text size="xs">Calendar</Text>
                      <Tooltip label="Learn how to connect calendar" withArrow>
                        <ActionIcon
                          variant="subtle"
                          size="sm"
                          component="a"
                          href="https://help.getdoublenickel.com/en/articles/11915438-connect-your-calendar-to-double-nickel"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <IconInfoCircle className="h-4 w-4 fill-none stroke-doubleNickel-gray-500" />
                        </ActionIcon>
                      </Tooltip>
                    </div>
                    {employee?.calendarConnected ? (
                      <Tooltip label="Calendar connected" withArrow>
                        <div>
                          <IconCheckmark className="h-4 w-4 fill-none stroke-doubleNickel-success-500" />
                        </div>
                      </Tooltip>
                    ) : (
                      <Tooltip label="Calendar not connected" withArrow>
                        <div>
                          <IconClose className="h-2 w-2 fill-none stroke-doubleNickel-error-500" />
                        </div>
                      </Tooltip>
                    )}
                  </div>
                  {isEditMode &&
                    (currentUser?.email === employee?.email ||
                      isAdminOrOpsUser) && (
                      <div className="flex items-center justify-between">
                        <div className="flex flex-col">
                          <Text size="xs" c="gray.6">
                            API Key
                          </Text>
                          <Text size="xs" c="gray.5">
                            {employee?.calendarAPIKeySet
                              ? 'Key on file. Paste a new key to replace it.'
                              : 'No key on file.'}
                          </Text>
                        </div>
                        <PasswordInput
                          placeholder={
                            employee?.calendarAPIKeySet
                              ? '•••••••••••• (set)'
                              : 'Paste cal.com API key'
                          }
                          size="xs"
                          className="w-[250px]"
                          classNames={{
                            input: 'text-xs text-right',
                          }}
                          autoComplete="new-password"
                          name="new-password"
                          value={calendarApiKey}
                          onChange={(e) => setCalendarApiKey(e.target.value)}
                          visibilityToggleIcon={() => null}
                        />
                      </div>
                    )}
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1">
                    <Text size="xs">Email</Text>
                    <Tooltip label="Learn how to connect email" withArrow>
                      <ActionIcon
                        variant="subtle"
                        size="sm"
                        component="a"
                        href="https://help.getdoublenickel.com/en/articles/12054212-connect-your-email-to-double-nickel"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <IconInfoCircle className="h-4 w-4 fill-none stroke-doubleNickel-gray-500" />
                      </ActionIcon>
                    </Tooltip>
                  </div>
                  {isEditMode && currentUser?.email === employee?.email ? (
                    <a
                      href={nylasAuthUrl}
                      onClick={() => {
                        // Store employee ID so we can reopen the drawer after OAuth
                        sessionStorage.setItem('nylas-employee-id', employeeId)
                      }}
                    >
                      <DNButton variant="secondary" size="xs">
                        {employee?.emailConnected
                          ? 'Reconnect Email'
                          : 'Connect Email'}
                      </DNButton>
                    </a>
                  ) : employee?.emailConnected ? (
                    <Tooltip label="Email connected" withArrow>
                      <div>
                        <IconCheckmark className="h-4 w-4 fill-none stroke-doubleNickel-success-500" />
                      </div>
                    </Tooltip>
                  ) : (
                    <Tooltip label="Email not connected" withArrow>
                      <div>
                        <IconClose className="h-2 w-2 fill-none stroke-doubleNickel-error-500" />
                      </div>
                    </Tooltip>
                  )}
                </div>
              </div>
            </div>

            <Divider variant="dashed" />

            {/* SECURITY Section */}
            <div className="space-y-4">
              <Text size="xs" fw={400} className="text-doubleNickel-gray-500">
                SECURITY
              </Text>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1">
                    <Text size="xs">Multi-Factor Authentication (MFA)</Text>
                    <Tooltip label="Learn how to enable MFA" withArrow>
                      <ActionIcon
                        variant="subtle"
                        size="sm"
                        component="a"
                        href="https://help.getdoublenickel.com/en/articles/12785601-add-multi-factor-authentication-mfa-to-double-nickel"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <IconInfoCircle className="h-4 w-4 fill-none stroke-doubleNickel-gray-500" />
                      </ActionIcon>
                    </Tooltip>
                  </div>
                  {isEditMode && currentUser?.email === employee?.email ? (
                    <div className="flex items-center gap-2">
                      <DNButton
                        variant="secondary"
                        size="xs"
                        onClick={() =>
                          employee?.mfaEnabled ? disableMFA() : enableMFA()
                        }
                        disabled={enablingMFA || disablingMFA}
                      >
                        {employee?.mfaEnabled ? 'Disable MFA' : 'Enable MFA'}
                      </DNButton>
                      {isAdminOrOpsUser && employee?.mfaEnabled && (
                        <DNButton
                          variant="outline"
                          size="xs"
                          onClick={() => resetMFA({ variables: { input: {} } })}
                          disabled={resettingMFA}
                          className="border-red-600 text-xs text-red-600 hover:bg-red-50"
                        >
                          Reset MFA
                        </DNButton>
                      )}
                    </div>
                  ) : employee?.mfaEnabled ? (
                    <Tooltip label="MFA enabled" withArrow>
                      <div>
                        <IconCheckmark className="h-4 w-4 fill-none stroke-doubleNickel-success-500" />
                      </div>
                    </Tooltip>
                  ) : (
                    <Tooltip label="MFA not enabled" withArrow>
                      <div>
                        <IconClose className="h-2 w-2 fill-none stroke-doubleNickel-error-500" />
                      </div>
                    </Tooltip>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {employeeId && (
        <PermissionHistoryModal
          opened={historyOpened}
          onClose={closeHistory}
          employeeId={employeeId}
          allPermissions={allPermissions}
        />
      )}
    </Drawer>
  )
}

export default UserDetailsDrawer
